import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export type OrderStatus = 'created' | 'paid' | 'processing' | 'ready' | 'completed' | 'cancelled';

export interface MyOrder {
  id: string;
  orderNumber: string;
  producerName: string;
  status: OrderStatus;
  createdAt: string;
  deliveryType: string;
  totalAmount: number;
  currency: string;
  itemCount: number;
  firstItemTitle: string | null;
  firstItemQuantity: number;
  firstProductId: string | null;
}

export const ORDER_STATUS_CONFIG: Record<
  OrderStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  created: { label: 'Beställning mottagen', variant: 'secondary' },
  paid: { label: 'Betald', variant: 'default' },
  processing: { label: 'Förbereds', variant: 'default' },
  ready: { label: 'Klar för upphämtning', variant: 'default' },
  completed: { label: 'Hämtad', variant: 'outline' },
  cancelled: { label: 'Avbruten', variant: 'destructive' },
};

export function useMyOrders() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['my-orders', user?.id],
    queryFn: async (): Promise<MyOrder[]> => {
      if (!user) return [];

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('id, external_order_id, total_amount, currency, status, delivery_type, created_at, producer_id')
        .eq('user_id', user.id)
        .neq('status', 'cancelled')
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;
      if (!ordersData?.length) return [];

      const producerIds = [...new Set(ordersData.map((o) => o.producer_id))];
      const orderIds = ordersData.map((o) => o.id);

      const [producersRes, itemsRes] = await Promise.all([
        supabase.from('producers').select('id, name').in('id', producerIds),
        supabase
          .from('order_items')
          .select('order_id, title, product_id, quantity')
          .in('order_id', orderIds)
          .order('created_at', { ascending: true }),
      ]);

      const producerMap = new Map(producersRes.data?.map((p) => [p.id, p.name]) ?? []);

      const itemMap = new Map<string, { count: number; firstTitle: string | null; firstQuantity: number; firstProductId: string | null }>();
      for (const item of itemsRes.data ?? []) {
        const existing = itemMap.get(item.order_id);
        if (existing) {
          existing.count++;
        } else {
          itemMap.set(item.order_id, { count: 1, firstTitle: item.title, firstQuantity: item.quantity ?? 1, firstProductId: item.product_id ?? null });
        }
      }

      return ordersData.map((order): MyOrder => {
        const items = itemMap.get(order.id);
        return {
          id: order.id,
          orderNumber: order.external_order_id || order.id.slice(0, 8).toUpperCase(),
          producerName: producerMap.get(order.producer_id) ?? 'Okänd gård',
          status: order.status as OrderStatus,
          createdAt: order.created_at,
          deliveryType: order.delivery_type,
          totalAmount: order.total_amount,
          currency: order.currency,
          itemCount: items?.count ?? 0,
          firstItemTitle: items?.firstTitle ?? null,
          firstItemQuantity: items?.firstQuantity ?? 1,
          firstProductId: items?.firstProductId ?? null,
        };
      });
    },
    enabled: !!user,
  });
}
